import {
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table as MUITable,
  TableRow as MUITableRow,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { TableBodyTypeMap } from '@mui/material/TableBody/TableBody';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Properties } from 'csstype';
import { isEmpty } from 'lodash';
import React, { MutableRefObject, ReactElement, SyntheticEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Column,
  HeaderGroup,
  TableToggleAllRowsSelectedProps,
  TableToggleRowsSelectedProps,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import styled from 'styled-components';
import usePrevious from '../../hooks/usePrevious';
import { EMPTY_ARRAY, PAGE_LIMIT } from '../../utils/constants';
import Skeleton from '../skeleton/Skeleton';
import TablePaginationActions from './TablePaginationActions';
import TableToolbar from './TableToolbar';
import { dateBetweenFilterFn } from './filters';
import { HTMLButtonElementExtended, TableProps, TableRowPropsExtended } from './interface';
import TableExport from './tableExport/TableExport';

const TableRowBody = styled(MUITableRow)(({ theme }) => ({
  '& .MuiTableCell-sizeSmall': {
    padding: 4,
  },
  [theme.breakpoints.up('md') as string]: {
    '& td': {
      fontSize: 14,
    },
  },
  [theme.breakpoints.up('md') as string]: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 8px',
    },
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: grey[50],
  },
  '&[data-link]': {
    cursor: 'pointer',
  },
}));

const TableRowHead = styled(MUITableRow)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  '& th': {
    fontWeight: 600,
  },
  '& .MuiTableCell-sizeSmall': {
    padding: 4,
  },
  [theme.breakpoints.up('md') as string]: {
    '& th': {
      fontSize: 14,
    },
  },
  [theme.breakpoints.up('md') as string]: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 8px',
    },
  },
}));
const TableRowTotals = styled(MUITableRow)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  '& td': {
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderTop: `2px solid ${theme.palette.divider}`,
    borderBottom: 'none',
  },
  '& .MuiTableCell-sizeSmall': {
    padding: 4,
  },
  [theme.breakpoints.up('md') as string]: {
    '& td': {
      fontSize: 14,
    },
  },
  [theme.breakpoints.up('md') as string]: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 8px',
    },
  },
}));

const TablePaginationStyled = styled(TablePagination)<{ disabled?: boolean }>(({ theme }) => ({
  padding: 0,
  margin: 0,
  '& .MuiTablePagination-toolbar': {
    minHeight: 0,
  },

  '& .MuiSelect-root': {
    marginRight: 8,
    marginLeft: 0,
  },
  [theme.breakpoints.up('md') as string]: {
    '& .MuiSelect-root': {
      marginRight: 32,
      marginLeft: 8,
    },
  },
}));

const BoxTable = styled(Box)`
  display: flex;
`;

const BoxTableIn = styled(Box)`
  flex: 1 1 auto;
  overflow-x: auto;
  width: 100px;
  border-radius: 3px;
`;

const BoxEllipsis = styled(Box)`
  display: flex;
  > span {
    flex: auto;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 10px;
  }
`;

const TableBodyStyled = styled(TableBody).withConfig({
  shouldForwardProp: (prop) => !['loading'].includes(prop),
})<OverridableComponent<TableBodyTypeMap> & { loading?: boolean }>`
  td {
    opacity: ${(props) => (props.loading ? 0.3 : 1)};
  }
`;

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }: { indeterminate?: boolean; children?: React.ReactNode }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = (ref || defaultRef) as MutableRefObject<HTMLButtonElementExtended>;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return <Checkbox ref={resolvedRef} {...rest} />;
});

export default function Table<T>({
  columns,
  data = EMPTY_ARRAY,
  dataExport = EMPTY_ARRAY,
  loading,
  updateMyData,
  skipPageReset,
  withSelect,
  title,
  withPagination = true,
  headerAddon,
  toolbarLeftAddon,
  defaultFilters,
  hideClearFilters,
  onFiltersClear,
  noGlobalFilter,
  size,
  toolbarDense,
  collapsable,
  paginationControl,
  pagination,
  defaultPageSize = 25,
  getRowProps,
  onRowsSelect,
  onStateChange,
  filtersAddon,
  getExportData,
  exportFileName,
  noDataText,
  defaultSelectedRowIds,
  rowIdKey,
  totals,
}: TableProps<T>): ReactElement {
  const navigate = useNavigate();
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const isSmDn = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const isMdDn = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [collapse, setCollapse] = useState(!collapsable);
  const [viewMode, setViewMode] = useState(true);
  const filterTypes = useMemo(
    () => ({
      dateBetween: dateBetweenFilterFn,
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase()) : true;
        });
      },
    }),
    [],
  );

  const dataToShow = useMemo(
    () =>
      loading && isEmpty(data)
        ? new Array(3).fill(0).map(() => {
            return columns.reduce((partData, column) => {
              const { align } = column as unknown as { align?: 'left' | 'right' };
              return { ...partData, [column.accessor as string]: <Skeleton randomWidth align={align} /> };
            }, {});
          })
        : data || [],
    [columns, loading, data],
  );

  const columnsToShow = useMemo(
    () =>
      loading && isEmpty(data)
        ? columns.map((column) => {
            const columnClear = { ...column } as Column & { Cell: unknown };
            delete columnClear.Cell;
            return columnClear;
          })
        : columns,
    [columns, loading, data],
  );

  const defaultSortBy = useMemo(() => {
    const targetColumn = columns.find(
      (column) => column.accessor === pagination?.sortBy?.replace(/^(-)/, '') || column.sortBy === pagination?.sortBy?.replace(/^(-)/, ''),
    ) as Column & {
      sortBy?: string;
    };
    return pagination?.sortBy && targetColumn?.accessor
      ? {
          sortBy: [
            {
              id: targetColumn.accessor as string,
              desc: pagination?.desc,
            },
          ],
        }
      : {};
  }, [pagination, columns]);

  const defaultSelectedRowIdsToUse = useMemo(() => {
    return defaultSelectedRowIds
      ? Object.keys(defaultSelectedRowIds).reduce((partIds, key) => {
          const targetIndex = rowIdKey ? data.findIndex((item) => item[rowIdKey] === key) : Number(key);
          return targetIndex >= 0 ? { ...partIds, [targetIndex]: true } : partIds;
        }, {})
      : defaultSelectedRowIds;
  }, [defaultSelectedRowIds, rowIdKey, data]);

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    // preGlobalFilteredRows,
    rows,
    setGlobalFilter,
    state,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy },
    setAllFilters,
  } = useTable(
    {
      columns: columnsToShow as readonly Column<Record<string, unknown>>[],
      data: dataToShow as readonly Record<string, unknown>[],
      filterTypes,
      autoResetPage: !skipPageReset,
      autoResetSortBy: !pagination,
      manualSortBy: !!pagination,
      // Server-paged tables always have a server-side default sort, so an
      // "unsorted" state is indistinguishable from it; toggle asc/desc only.
      disableSortRemove: !!pagination,
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      ...(updateMyData ? { updateMyData } : {}),
      initialState: {
        ...(defaultFilters && { filters: defaultFilters }),
        ...(defaultSelectedRowIds && { selectedRowIds: defaultSelectedRowIdsToUse }),
        ...(!withPagination || pagination ? { pageSize: PAGE_LIMIT } : { pageSize: defaultPageSize }),
        ...defaultSortBy,
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (withSelect) {
        hooks.allColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox.  Pagination is a problem since this will select all
            // rows even though not all rows are on the current page.  The solution should
            // be server side pagination.  For one, the clients should not download all
            // rows in most cases.  The client should only download data for the current page.
            // In that case, getToggleAllRowsSelectedProps works fine.

            Header: ({
              getToggleAllRowsSelectedProps,
            }: {
              getToggleAllRowsSelectedProps: (props?: Partial<TableToggleAllRowsSelectedProps>) => TableToggleAllRowsSelectedProps;
            }) => <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />,
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }: { row: { getToggleRowSelectedProps: (props?: Partial<TableToggleRowsSelectedProps>) => TableToggleRowsSelectedProps } }) => (
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            ),
            width: 50,
          },
          ...columns,
        ]);
      }
    },
  );

  const prevSortBy = usePrevious(sortBy);
  useEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);

  useEffect(() => {
    const targetColumn = columns.find((column) => column.accessor === sortBy?.[0]?.id);
    const prevTargetColumn = columns.find((column) => column.accessor === prevSortBy?.[0]?.id);

    const id = targetColumn?.sortBy || sortBy?.[0]?.id;
    const prevId = prevTargetColumn?.sortBy || prevSortBy?.[0]?.id;

    // prevSortBy is undefined on the first render: the initial sort came from the
    // caller (URL param or a default), so there is nothing to push back out.
    if (paginationControl && prevSortBy !== undefined && (prevId !== id || prevSortBy?.[0]?.desc !== sortBy?.[0]?.desc)) {
      paginationControl.setSort(id ? [{ id, desc: sortBy[0]?.desc ? sortBy[0]?.desc : false }] : []);
    }
  }, [paginationControl, sortBy, prevSortBy, columns]);

  // Total record count: the server-side count when paged remotely, else the local row count.
  const totalRowCount = paginationControl && pagination?.count !== undefined ? pagination.count : rows.length;
  const showTotals = !!totals && !loading && !isEmpty(data) && !isEmpty(rows);

  const handleChangePage = (event, newPage) => {
    if (paginationControl) {
      paginationControl.setPage(newPage);
    } else {
      gotoPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (paginationControl) {
      paginationControl.setLimit(Number(event.target.value));
    } else {
      setPageSize(Number(event.target.value));
    }
  };

  const gotFilters = useMemo(() => !!headerGroups.find((headerGroup) => headerGroup.headers.find((column) => column.Filter)), [headerGroups]);

  const handleClearFilters = useCallback(() => {
    setAllFilters([]);
    if (onFiltersClear) {
      onFiltersClear();
    }
  }, [setAllFilters, onFiltersClear]);

  const handleRowClick = useCallback(
    (event: SyntheticEvent) => {
      const { dataset } = event.currentTarget as HTMLDivElement;

      if (dataset.link && (event.target as HTMLElement).tagName.toLowerCase() !== 'a') {
        navigate(dataset.link);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (onRowsSelect) {
      onRowsSelect(selectedRowIds);
    }
  }, [onRowsSelect, selectedRowIds]);

  return (
    <TableContainer>
      {((globalFilter && !noGlobalFilter) || title || headerAddon) && (
        <TableToolbar
          numSelected={Object.keys(selectedRowIds).length}
          rows={rows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
          title={title}
          loading={loading}
          headerAddon={headerAddon}
          leftAddon={toolbarLeftAddon}
          noGlobalFilter={noGlobalFilter}
          dense={toolbarDense}
          collapse={collapse}
          collapsable={collapsable}
          setCollapse={setCollapse}
        />
      )}
      <Collapse in={collapse}>
        {(gotFilters || filtersAddon || getExportData) && (
          <React.Fragment>
            <Card sx={{ py: size === 'small' ? 1 : 3, pt: !title && noGlobalFilter ? 0 : size === 'small' ? 1 : 3 }}>
              <Typography variant="subtitle1">
                <Grid container alignItems="center" justifyContent="flex-end">
                  {gotFilters && (
                    <React.Fragment>
                      {headerGroups.map((headerGroup, index) =>
                        headerGroup.headers
                          .filter((column: HeaderGroup) => column.Filter)
                          .map((column: HeaderGroup & { filterOrder?: number; filterProps?: { [key: string]: string | number | Record<string, unknown> } }) => (
                            <Grid
                              item
                              key={column.id}
                              order={column.filterOrder}
                              {...(isSmDn ? { xs: 12, sx: { px: 3 } } : { sx: { pl: index === 0 ? 0 : 6, pr: 2 } })}
                            >
                              {column.render('Filter', { viewMode: true, ...column.filterProps })}
                            </Grid>
                          )),
                      )}
                      <Grid item sx={{ pl: 6, pr: 3 }}>
                        <Button variant="outlined" color="secondary" onClick={() => setViewMode(false)}>
                          Filters
                        </Button>
                      </Grid>
                    </React.Fragment>
                  )}
                  {filtersAddon && (
                    <Grid item sx={{ pr: 3 }}>
                      {filtersAddon}
                    </Grid>
                  )}
                  {getExportData && (
                    <TableExport
                      pagination={pagination}
                      columns={columns}
                      data={dataExport}
                      loading={loading}
                      exportFileName={exportFileName}
                      getExportData={getExportData}
                    />
                  )}
                </Grid>
              </Typography>
            </Card>
            <Dialog open={!viewMode} maxWidth={false} onClose={() => setViewMode(true)}>
              <DialogTitle>Edit Filters</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} flexWrap="wrap" alignItems="flex-end" justifyContent="flex-end" sx={{ pt: 2 }}>
                  {headerGroups.map((headerGroup, index) =>
                    headerGroup.headers
                      .filter((column: HeaderGroup) => column.Filter && typeof column.Filter === 'function')
                      .map((column: HeaderGroup & { filterOrder?: number; filterProps?: { [key: string]: string | number | Record<string, unknown> } }) => (
                        <Grid
                          item
                          key={column.id}
                          order={column.filterOrder}
                          {...(isMdDn ? { xs: 12, sx: { px: 3, py: 2 } } : { sx: { pl: index === 0 ? 0 : 6 } })}
                        >
                          {column.render('Filter', { ...column.filterProps, fullWidth: isMdDn })}
                        </Grid>
                      )),
                  )}
                  <Grid item sx={{ pl: 6 }}>
                    <Stack spacing={2} direction="row">
                      {!hideClearFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} sx={{ whiteSpace: 'nowrap' }}>
                          Clear Filters
                        </Button>
                      )}
                      <Button variant="outlined" color="secondary" onClick={() => setViewMode(true)}>
                        Close
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </DialogContent>
            </Dialog>
          </React.Fragment>
        )}

        <BoxTable>
          <BoxTableIn>
            <MUITable {...getTableProps()} {...(!lg && !size && { size: 'small' })} {...(size && { size })}>
              <TableHead>
                {headerGroups.map((headerGroup) => (
                  <TableRowHead {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column: HeaderGroup & { style?: Properties }) => {
                      const targetColumn = columnsToShow.find((item) => item.accessor === column.id);
                      return (
                        <TableCell
                          {...(column.id === 'selection' ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps()))}
                          align={(column as { align?: 'inherit' | 'center' | 'left' | 'right' | 'justify' | undefined }).align}
                          {...column.getHeaderProps({
                            style: {
                              ...column.style,
                              ...(targetColumn?.minWidth && { minWidth: targetColumn.minWidth }),
                              ...(targetColumn?.width && { width: targetColumn.width }),
                            },
                          })}
                        >
                          {(column as { align?: 'inherit' | 'center' | 'left' | 'right' | 'justify' | undefined }).align !== 'right' && column.render('Header')}
                          {column.id !== 'selection' && !column.disableSortBy ? (
                            <TableSortLabel
                              active={column.isSorted}
                              // react-table has a unsorted state which is not treated here
                              direction={column.isSortedDesc ? 'desc' : 'asc'}
                            />
                          ) : null}
                          {(column as { align?: 'inherit' | 'center' | 'left' | 'right' | 'justify' | undefined }).align === 'right' && column.render('Header')}
                        </TableCell>
                      );
                    })}
                  </TableRowHead>
                ))}
              </TableHead>
              <TableBodyStyled loading={loading}>
                {((!page.length && globalFilter) || (!loading && isEmpty(data)) || (!loading && isEmpty(rows))) && (
                  <TableRowBody>
                    <TableCell colSpan={columnsToShow.length + (withSelect ? 1 : 0)} align="center">
                      <Typography variant="h6" sx={{ p: 2 }}>
                        {!loading && isEmpty(data) ? noDataText ?? 'No Data' : 'Nothing found'}
                      </Typography>
                    </TableCell>
                  </TableRowBody>
                )}
                {page.map((row) => {
                  prepareRow(row);
                  const { link, ...rowProps } = row.getRowProps(getRowProps ? getRowProps(row) : undefined) as TableRowPropsExtended;
                  return (
                    <TableRowBody {...rowProps} {...(link && { onClick: handleRowClick, 'data-link': link })}>
                      {row.cells.map((cell) => {
                        return (
                          <TableCell
                            {...cell.getCellProps({
                              style: {
                                ...((cell.column as { style?: Record<string, unknown> }).style
                                  ? (cell.column as { style?: Record<string, unknown> }).style
                                  : {}),
                              },
                            })}
                            align={(cell.column as { align?: 'inherit' | 'center' | 'left' | 'right' | 'justify' | undefined }).align}
                          >
                            {cell.column.ellipsis ? (
                              <BoxEllipsis>
                                <span>{cell.render('Cell')}</span>
                              </BoxEllipsis>
                            ) : (
                              cell.render('Cell')
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRowBody>
                  );
                })}
              </TableBodyStyled>

              {(showTotals || withPagination) && (
                <TableFooter>
                  {showTotals && (
                    <TableRowTotals data-testid="table-totals">
                      {headerGroups[headerGroups.length - 1].headers.map((column, index) => {
                        const { align, style } = column as { align?: 'inherit' | 'center' | 'left' | 'right' | 'justify'; style?: Properties };
                        const firstDataColumn = index === (withSelect ? 1 : 0);
                        return (
                          <TableCell key={column.id} align={align} style={style}>
                            {firstDataColumn ? `${totalRowCount} ${totalRowCount === 1 ? 'row' : 'rows'}` : totals?.[column.id] ?? ''}
                          </TableCell>
                        );
                      })}
                    </TableRowTotals>
                  )}
                  {withPagination && (
                    <MUITableRow>
                      {loading && isEmpty(data) ? (
                        <TableCell align="right" colSpan={columnsToShow.length}>
                          <Skeleton align="right" width={300} />
                        </TableCell>
                      ) : (
                        <TablePaginationStyled
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          count={paginationControl && pagination?.count !== undefined ? pagination?.count : rows.length}
                          rowsPerPage={paginationControl && pagination?.limit !== undefined ? pagination?.limit : pageSize}
                          page={paginationControl && pagination?.page !== undefined ? pagination?.page : pageIndex}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                          disabled
                        />
                      )}
                    </MUITableRow>
                  )}
                </TableFooter>
              )}
            </MUITable>
          </BoxTableIn>
        </BoxTable>
      </Collapse>
    </TableContainer>
  );
}
